// Lazy import storage only on client side
let storage: any = null;

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  if (!storage) {
    try {
      storage = require('@/lib/storage').storage;
    } catch (e) {
      return null;
    }
  }
  return storage;
};

export interface PortfolioProject {
  id: string;
  title: string;
  category: string;
  tags: string[];
  coverImage?: string;
  image?: string;
  alt?: string;
  challenge: string;
  solution: string;
  results: string;
  impact?: string[];
  techStack?: string[];
  clientName?: string;
  client?: string;
  clientTestimonial?: string;
  liveUrl?: string;
  status?: 'draft' | 'published';
  featured?: boolean;
  year?: number;
  description?: string;
  fullDescription?: string;
  result?: string;
}

// Default projects data (40+ projects for fallback)
const defaultProjects: PortfolioProject[] = [
  {
    id: 'proj-001',
    title: 'Meridian Finance Dashboard',
    category: 'Web',
    tags: ['React', 'TypeScript', 'Recharts'],
    description: 'Real-time financial analytics platform handling $2.4B in transactions monthly.',
    fullDescription: 'Meridian Finance built a comprehensive real-time analytics platform to track and visualize financial transactions. Our solution handles 2.4 billion dollars in monthly transactions with sub-second latency, providing institutional investors with actionable insights.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b09aca67-1772313899728.png',
    alt: 'Finance dashboard with charts and analytics data on dark background',
    result: '+340% user retention',
    featured: true,
    year: 2024,
    client: 'Meridian Finance Inc.',
    challenge: 'Building a scalable financial dashboard that could handle massive transaction volumes while maintaining real-time updates and accuracy.',
    solution: 'Implemented a React TypeScript architecture with Recharts for data visualization, combined with efficient data aggregation pipelines.',
    results: '+340% improvement in user retention, $2.4B in monthly transaction volume, <500ms query response time',
    impact: [
      '+340% improvement in user retention',
      '$2.4B in monthly transaction volume',
      '<500ms query response time'
    ],
    status: 'published',
  },
  {
    id: 'proj-002',
    title: 'Orbis Health App',
    category: 'Mobile App',
    tags: ['React Native', 'Node.js', 'AI'],
    description: 'AI-powered wellness app with 280k active users across iOS and Android.',
    fullDescription: 'Orbis Health created an intelligent wellness application that uses AI to provide personalized health recommendations. The app has grown to 280,000 active users with a 4.9-star rating.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f9e0c16d-1764676756614.png',
    alt: 'Health app interface showing fitness tracking and wellness metrics on mobile',
    result: '280k active users',
    featured: false,
    year: 2024,
    client: 'Orbis Health',
    challenge: 'Creating a cross-platform health app that provides AI-driven insights without compromising user privacy or battery life.',
    solution: 'Built with React Native for iOS and Android, Node.js backend for processing, and on-device ML models for privacy-first recommendations.',
    results: '280,000+ active users, 4.9-star app store rating, 95% monthly retention rate',
    impact: [
      '280,000+ active users',
      '4.9-star app store rating',
      '95% monthly retention rate'
    ],
    status: 'published',
  },
  {
    id: 'proj-003',
    title: 'Kairos Brand Identity',
    category: 'Branding',
    tags: ['Brand Strategy', 'Logo', 'Guidelines'],
    description: 'Complete rebrand for a Series B SaaS company entering enterprise markets.',
    fullDescription: 'Kairos underwent a complete brand transformation to position themselves for enterprise adoption. We developed a comprehensive brand identity that conveys trust and innovation.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1458eacee-1776876363999.png',
    alt: 'Brand identity design with logo variations and color palette on white background',
    result: '4.2x brand recall lift',
    featured: false,
    year: 2024,
    client: 'Kairos (Series B SaaS)',
    challenge: 'Repositioning a B2B SaaS company from a startup brand to an enterprise-grade solution.',
    solution: 'Developed a sophisticated brand system with modern aesthetics, comprehensive brand guidelines, and enterprise-focused messaging.',
    results: '4.2x brand recall lift, 340% increase in enterprise inquiries, Successful Series B close',
    impact: [
      '4.2x brand recall lift',
      '340% increase in enterprise inquiries',
      'Successful Series B close'
    ],
    status: 'published',
  },
  {
    id: 'proj-004',
    title: 'Vanta E-Commerce Platform',
    category: 'Web',
    tags: ['Next.js', 'Shopify', 'Tailwind'],
    description: 'Custom headless commerce storefront generating $8M ARR in year one.',
    fullDescription: 'Vanta built a high-performance e-commerce platform using headless commerce architecture, generating $8 million in annual recurring revenue in its first year.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d0d24d24-1771424896215.png',
    alt: 'E-commerce product page with clean minimal design and product photography',
    result: '$8M ARR Year 1',
    featured: true,
    year: 2023,
    client: 'Vanta Commerce',
    challenge: 'Creating a high-converting e-commerce experience that could scale with rapid growth.',
    solution: 'Implemented a headless Next.js storefront with Shopify backend, optimized for conversion and performance.',
    results: '$8M ARR in year 1, 3.2% checkout conversion rate, Average order value +45%',
    impact: [
      '$8M ARR in year 1',
      '3.2% checkout conversion rate',
      'Average order value +45%'
    ],
    status: 'published',
  },
  {
    id: 'proj-005',
    title: 'Pulse Social Campaign',
    category: 'Marketing',
    tags: ['Meta Ads', 'Creative', 'Analytics'],
    description: 'Multi-channel launch campaign for a DTC brand — 4.8x ROAS in 90 days.',
    fullDescription: 'Pulse executed a comprehensive social media campaign across Meta platforms, achieving exceptional ROI through creative excellence and data-driven optimization.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1941d5aac-1772570678890.png',
    alt: 'Social media campaign creative assets with vibrant colors and bold typography',
    result: '4.8x ROAS achieved',
    featured: false,
    year: 2024,
    client: 'Pulse DTC Brand',
    challenge: 'Launching a new DTC brand with limited awareness in a competitive market.',
    solution: 'Developed creative-first strategy with data-driven optimization across Meta platform.',
    results: '4.8x ROAS, 2.3M impressions, 145K conversions',
    impact: [
      '4.8x ROAS',
      '2.3M impressions',
      '145K conversions'
    ],
    status: 'published',
  },
  {
    id: 'proj-006',
    title: 'Nexus Mobile Banking',
    category: 'Mobile App',
    tags: ['Flutter', 'Firebase', 'Fintech'],
    description: 'Neobank app with biometric auth, instant transfers, and smart budgeting.',
    fullDescription: 'Nexus developed a modern neobank app with cutting-edge security features and an intuitive UX, attracting 150k downloads in the first 3 months.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10035b821-1773048697275.png',
    alt: 'Mobile banking app with transaction history and account balance on smartphone',
    result: '150k downloads, 4.9★',
    featured: false,
    year: 2023,
    client: 'Nexus Finance',
    challenge: 'Building a secure, user-friendly fintech app in a competitive neobank market.',
    solution: 'Created Flutter-based app with Firebase backend, biometric authentication, and real-time transaction processing.',
    results: '150,000 downloads, 4.9-star app rating, $2M TVL in first year',
    impact: [
      '150,000 downloads',
      '4.9-star app rating',
      '$2M TVL in first year'
    ],
    status: 'published',
  },
  {
    id: 'proj-007',
    title: 'Solace Mental Health Platform',
    category: 'Web',
    tags: ['Vue.js', 'Python', 'Healthcare'],
    description: 'HIPAA-compliant teletherapy platform connecting 5,000+ therapists with patients.',
    fullDescription: 'Solace built a HIPAA-compliant teletherapy platform that revolutionized how patients connect with mental health professionals, achieving 5,200+ therapist onboarding.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_13b708668-1766923037310.png',
    alt: 'Mental health platform with therapist matching interface and calm blue design',
    result: '5,200 therapists onboarded',
    featured: false,
    year: 2023,
    client: 'Solace Healthcare',
    challenge: 'Building a HIPAA-compliant healthcare platform with seamless therapist-patient matching.',
    solution: 'Developed Vue.js frontend with Python backend, comprehensive security protocols, and intelligent matching algorithm.',
    results: '5,200+ therapists onboarded, 48K+ active patients, 98% patient satisfaction',
    impact: [
      '5,200+ therapists onboarded',
      '48K+ active patients',
      '98% patient satisfaction'
    ],
    status: 'published',
  },
  {
    id: 'proj-008',
    title: 'Terroir Restaurant Branding',
    category: 'Branding',
    tags: ['Visual Identity', 'Print', 'Packaging'],
    description: 'Michelin-star restaurant brand system from naming through full collateral suite.',
    fullDescription: 'Terroir received comprehensive branding from naming strategy through full collateral suite, culminating in Michelin-star recognition.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_164d82dd3-1776876365044.png',
    alt: 'Restaurant branding with elegant menu design and sophisticated visual identity',
    result: 'Michelin star awarded',
    featured: false,
    year: 2024,
    client: 'Terroir Restaurant Group',
    challenge: 'Creating a sophisticated brand identity for a fine dining establishment.',
    solution: 'Developed comprehensive brand system with elegant visual identity, premium collateral, and strategic positioning.',
    results: 'Michelin star awarded, Industry recognition, 6-month waitlist achieved',
    impact: [
      'Michelin star awarded',
      'Industry recognition',
      '6-month waitlist achieved'
    ],
    status: 'published',
  },
  ...Array.from({ length: 32 }, (_, i) => ({
    id: `proj-${String(i + 9).padStart(3, '0')}`,
    title: `Project ${i + 9}: ${['Enterprise Dashboard', 'Mobile Solution', 'Brand Refresh', 'Campaign', 'Platform', 'App'][i % 6]}`,
    category: ['Web', 'Mobile App', 'Branding', 'Marketing'][i % 4],
    tags: ['Design', 'Development', 'Strategy'],
    description: `High-impact project delivered for leading ${['fintech', 'healthcare', 'ecommerce', 'tech'][i % 4]} company.`,
    fullDescription: `This project showcases our expertise in delivering comprehensive solutions for complex business challenges.`,
    image: `https://img.rocket.new/generatedImages/rocket_gen_img_${i + 1}-placeholder.png`,
    alt: `Project ${i + 9} preview`,
    result: `${Math.floor(Math.random() * 300) + 50}% impact`,
    featured: false,
    year: 2023 + (i % 2),
    client: `Client ${i + 9}`,
    challenge: `Building solutions for modern business challenges in the ${['fintech', 'healthcare', 'ecommerce', 'tech'][i % 4]} space.`,
    solution: `Implemented strategic approach using best-in-class technologies and proven methodologies.`,
    results: `Delivered exceptional results through strategic implementation.`,
    impact: [`Result 1: Improved performance`, `Result 2: Enhanced user experience`, `Result 3: Business growth`],
    status: 'published',
  } as PortfolioProject)),
];

// Export the raw projectsData for static generation
export const projectsData = defaultProjects;

/**
 * Get all published projects from admin storage or fall back to defaults
 * Used for public portfolio display
 */
export function getPublishedProjects(): PortfolioProject[] {
  if (typeof window === 'undefined') {
    // Server-side: return defaults
    return defaultProjects.filter(p => p.status !== 'draft');
  }

  try {
    const storageMod = getStorage();
    if (storageMod) {
      const adminProjects = storageMod.getPortfolioProjects();
      if (adminProjects && adminProjects.length > 0) {
        return adminProjects.filter(p => p.status !== 'draft');
      }
    }
  } catch (error) {
    console.warn('Failed to load projects from storage:', error);
  }

  return defaultProjects.filter(p => p.status !== 'draft');
}

/**
 * Get all projects including drafts
 * Used for admin dashboard
 */
export function getAllProjects(): PortfolioProject[] {
  if (typeof window === 'undefined') {
    return defaultProjects;
  }

  try {
    const storageMod = getStorage();
    if (storageMod) {
      const adminProjects = storageMod.getPortfolioProjects();
      if (adminProjects && adminProjects.length > 0) {
        return adminProjects;
      }
    }
  } catch (error) {
    console.warn('Failed to load projects from storage:', error);
  }

  return defaultProjects;
}

/**
 * Get a single project by ID
 */
export function getProjectById(id: string): PortfolioProject | undefined {
  return getAllProjects().find(p => p.id === id);
}

/**
 * Get projects filtered by category
 */
export function getProjectsByCategory(category: string): PortfolioProject[] {
  return getPublishedProjects().filter(p => p.category === category);
}

/**
 * Get unique categories from all published projects
 */
export function getCategories(): string[] {
  const categories = new Set<string>();
  getPublishedProjects().forEach(p => {
    if (p.category) categories.add(p.category);
  });
  return Array.from(categories).sort();
}
